// WHO CHANGED THIS CLAIM. THE FIELD THAT DID NOT EXIST.
//
// WHY THIS FILE EXISTS. `data\YCP_AUDIT_TRAIL_3.6_2026-08-31.md` read the audit trail end to end and
// found three things. The rule at `firestore.rules:609` is real and sound. FOUR call sites write to
// `audit_logs`, all of them inside `src\data\agreementsRepo.js`, and NONE of them is a claim. And
// `updatedBy` does not exist anywhere in the codebase - so even where `updatedAt` moves, nothing
// says whose hand moved it.
//
// S-7 counted it from the other direction: of 72 mutation sites in `src\`, 41 record nothing but the
// mutation itself, and 27 of those change a record that already exists. The ones a duty-of-fair-
// representation challenge actually asks about are all in that 27 - suspend, reactivate, reassign
// jurisdiction, return, present, file, delete a document.
//
// RULING 79, 2026-08-31: PROTECT THE MEMBER FIRST. Ruling 86 then settled what the record is FOR -
// the member can see the history of his own claim, every officer action, who and when. Bob's own
// words are the design brief: "As a member, I'd be happy to hear it, never believe it." A promise
// that everything is recorded is worth nothing to a member who cannot check it.
//
// WHY A HELPER AND NOT 21 HAND-EDITS. `src\data\memberProfile.js` already established this shape in
// this codebase: one small pure function that every write passes through, and a verifier check
// (`PRF-1`) that proves every write DOES pass through it. A field added by hand at 21 sites is a
// field that is missing from the 22nd. A helper is checkable.
//
// WHAT THIS DOES NOT DO, SAID HERE SO NOBODY READS IT AS MORE THAN IT IS:
//
//   - It does not make attribution UNFORGEABLE. That is the rules clause, held UNDEPLOYED in
//     `data\firestore.rules.updatedby_target_2026-08-31` until every site is routed through here.
//     Until that clause is live this is a convention, and ISS-43 is right that a client-side
//     convention is a convention.
//   - It does not record a DIFF. That is route (b), the onWrite trigger, and it comes after this.
//   - It does nothing for READS. `AUD-1` states that limit and it is unchangeable: Firestore does
//     not journal reads.
//
// WHY IT DOES NOT THROW WHEN THERE IS NO ACTOR. An officer's action failing in production because a
// uid was not threaded through some screen is a live break, and PD-4 forbids writing a value that is
// not true. So the field is OMITTED rather than blanked or guessed. An omitted `updatedBy` is a
// write the rules clause will DENY the moment it is deployed - which is the correct failure, at the
// correct layer, and it is visible. A blank string would have been stored and would have looked
// like an answer.

import { claimExampleStamp } from './testTerritory';   // Record 662: a tester's claim stays in the test territory

/**
 * Stamp a claim patch with the actor who is making the change.
 *
 * @param {object} patch  what the caller is about to write to claims/{id}
 * @param {object|string} actor  the signed-in user object, or a uid string
 * @returns {object} the same patch with `updatedBy` set, or unchanged if no uid is available
 */
export function claimPatch(patch, actor) {
  const uid = actorUid(actor);
  const out = { ...(patch || {}) };
  if (uid) out.updatedBy = uid;
  // Record 662: a TESTER's full claim write (one that carries scopeKey - a filing or a draft save) is kept in the test
  // territory and marked EXAMPLE ONLY, whatever the form routed it to. A tester whose sign-in lacks the territory is
  // refused here with the same words the discipline screen uses; nothing is written.
  if (actor && typeof actor === 'object' && 'scopeKey' in out) {
    const ex = claimExampleStamp(actor);
    if (!ex.ok) throw new Error(ex.why);
    Object.assign(out, ex.stamp);
  }
  return out;
}

/**
 * The uid of whoever is acting, from a user object or a bare string. Empty string when unknown -
 * never null, never undefined, so a caller can test it without optional chaining.
 * @param {object|string} actor
 * @returns {string}
 */
export function actorUid(actor) {
  if (!actor) return '';
  if (typeof actor === 'string') return actor.trim();
  const v = actor.uid || actor.id || '';
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Is this patch attributed? Used by the tests and by anything that wants to refuse to send an
 * unattributed write rather than let the rules refuse it later.
 * @param {object} patch
 * @returns {boolean}
 */
export function isAttributed(patch) {
  const v = patch && patch.updatedBy;
  return typeof v === 'string' && v.trim().length > 0;
}

// The claim fields a member is entitled to see the history OF (ruling 86). Not used to filter
// anything yet - it is here so that the member-history design and the trigger that will populate it
// agree on one list instead of drifting into two (SS XI).
//
// DELIBERATELY NOT INCLUDED: `notes`. Ruling 86.2 leaves open whether an officer's deliberative note
// is the member's to read, and ruling 69's audited review record depends on officers writing
// candidly. That line is Bob's to draw and it is not drawn here.
export const MEMBER_VISIBLE_ACTIONS = [
  'status',
  'stage',
  'scopeKey',
  'committee',
  'routingStatus',
  'expiresAt',
  'claimNumber',
  'outcome',
  'presentedAt',
  'returnedAt',
];
